<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToTree'
});
</script>
<template>
	<div :key="langKey" :flex="flex ? flex : null" class="to-tree" :class="setClass">
		<template v-if="filterStore.length">
			<to-tree-item v-for="item in filterStore" :key="item[props.valueText]" :node="item" :item-props="props">
				<template #default="scope">
					<slot :item="scope.node"></slot>
				</template>
			</to-tree-item>
		</template>
		<div v-else class="to-tree-empty">
			{{ props.emptyText || t('to.tree.noData') }}
		</div>
	</div>
</template>
<script lang="ts" setup>
import { Ref, computed, provide, ref, watch } from 'vue'
import ToTreeItem from './tree-item.vue'
import { deepClone } from '@touchvue/utils'
import { t, langKey } from '@touchvue/locale'



export type TreeKeys = string | number

export interface Node {
	label: string
	value: string
	icon?: string
	children?: Node[]
	isLeaf?: boolean
	isExpand?: boolean
	checked?: boolean
	isSelect?: boolean
	loading?: boolean
	isLoad?: boolean
	[key: string]: any
}

export interface Props {
	flex?: boolean
	mode?: string
	expand?: boolean
	data: Node[]
	modelValue?: Node[] | string
	toggleLink?: false
	checkLink?: false
	checkable?: false
	selectable?: false
	checkStrictly?: false
	accordion?: false
	childrenText?: string
	valueText?: string
	labelText?: string
	emptyText?: string
	load?: (node: any) => any | null
	parentSelectable?: boolean
	filterNodeMethod?: (key, node) => any | null
}

const props = withDefaults(defineProps<Props>(), {
	mode: 'default',
	expand: false, //默认是否全部展开
	data: () => [],
	toggleLink: false, //是否可点击节点展开收缩
	checkLink: false, //是否可点击节点选中
	selectable: false, //节点是否可被选择
	checkable: false, //是否显示节点复选框
	checkStrictly: false, //父子是否强关联
	accordion: false, //是否每次只打开一个同级树节点
	childrenText: 'children',
	valueText: 'value',
	labelText: 'label',
	modelValue: '',
	emptyText: '',
	load: null,
	parentSelectable: false,
	filterNodeMethod: null
})

const stopChange = ref(false)

const valueType = computed(() => {
	return typeof props.modelValue
})
const store = ref(initData(props.data, 0))

const selectValues = computed(() => {
	let values = []
	if (props.checkable) {
		values = getCheckValues(store.value)
	}
	if (props.selectable) {
		values = getCurrentNode(store.value)
	}
	return values
})
const initValues = ref([])

const setClass = computed(() => {
	let arr = []
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	return arr
})

watch(
	() => store.value,
	newVal => {
		if (!props.checkStrictly) {
			setParentCheck(newVal)
		}
	},
	{ deep: true }
)

watch(
	() => props.data,
	newVal => {
		store.value = initData(newVal, 0)
		initCheckValues(props.modelValue)
	},
	{ deep: true }
)

watch(
	() => props.modelValue,
	newVal => {
		if (stopChange.value) {
			return
		}
		if (newVal) {
			if (Array.isArray(newVal)) {
				if (newVal.length) {
					initCheckValues(newVal)
				} else {
					if (props.checkable) {
						setCheckByValues(store.value, [])
					}
					if (props.selectable) {
						setCurrentNode(store.value, '')
					}
					if (!props.expand) {
						setExpandByKeys(store.value, [])
					}
				}
			} else {
				initCheckValues(newVal)
			}
		} else {
			if (props.checkable) {
				setCheckByValues(store.value, [])
			}
			if (props.selectable) {
				setCurrentNode(store.value, '')
			}
			if (!props.expand) {
				setExpandByKeys(store.value, [])
			}
		}
	},
	{ immediate: true, deep: true }
)

watch(
	() => selectValues.value,
	newVal => {
		initValues.value = newVal
		updateModelValue()
	}
)

function updateModelValue() {
	if (selectValues.value.length === 0) {
		if (valueType.value === 'string') {
			emit('update:modelValue', initValues.value.join(',') || '')
		} else if (valueType.value === 'object' && Array.isArray(props.modelValue)) {
			emit('update:modelValue', initValues.value || [])
		}
		return
	}
	if (valueType.value === 'string') {
		emit('update:modelValue', selectValues.value.join(','))
	} else if (valueType.value === 'object' && Array.isArray(props.modelValue)) {
		emit('update:modelValue', selectValues.value)
	} else {
		console.warn('请设置正确的数据类型')
	}
}
function initData(data: Node[], levelPlus: number): Node[] {
	let tem = deepClone(data)
	let arr = []
	let level = levelPlus
	tem.forEach((item: Node) => {
		if (item[props.childrenText] && item[props.childrenText].length) {
			arr.push({
				...item,
				isLeaf: false,
				isExpand: props.expand,
				checked: false,
				level,
				isSelect: false,
				loading: false,
				isNeedLoad: !!props.load,
				[props.childrenText]: initData(item[props.childrenText], level + 1)
			})
		} else {
			arr.push({
				...item,
				level,
				isLeaf: true,
				checked: false,
				isSelect: false,
				loading: false,
				isNeedLoad: !!props.load,
				isExpand: props.expand
			})
		}
	})
	return arr
}

function initCheckValues(newVal) {
	let checkValues = []
	if (typeof newVal === 'string') {
		checkValues = newVal.split(',')
	} else if (Array.isArray(newVal)) {
		checkValues = newVal
	} else if (typeof newVal === 'number') {
		checkValues = [newVal]
	}
	initValues.value = checkValues
	if (checkValues.length) {
		if (props.checkable) {
			setStopChange(true)
			setCheckByValues(store.value, checkValues)
			setTimeout(() => {
				setStopChange(false)
			}, 10)
		}
		let arr = []
		checkValues.forEach(value => {
			if (props.selectable) {
				setCurrentNode(store.value, value)
			}
			if (props.expand) return false
			let tem = getParentByValue(store.value, value)
			let values = tem.map(item => item[props.valueText])
			arr = [...arr, ...values]
			let expandValues = Array.from(new Set(arr))
			setExpandByValues(store.value, expandValues)
		})
	}
}

function setCheckByValues(arr, values) {
	for (const item of arr) {
		if (values.includes(item[props.valueText])) {
			if (!props.checkStrictly) {
				if (item.isLeaf) {
					item.checked = true
				} else {
					item.checked = false
					setCheckByValues(item[props.childrenText], values)
				}
			} else {
				item.checked = true
				if (item[props.childrenText] && item[props.childrenText].length) {
					setCheckByValues(item[props.childrenText], values)
				}
			}
		} else {
			item.checked = false
			if (item[props.childrenText] && item[props.childrenText].length > 0) {
				// 递归查找子节点
				setCheckByValues(item[props.childrenText], values)
			}
		}
	}
}

function setExpandByValues(arr, values) {
	for (const item of arr) {
		if (values.includes(item[props.valueText])) {
			item.isExpand = true
		} else {
			item.isExpand = false
		}
		if (item[props.childrenText] && item[props.childrenText].length > 0) {
			// 递归查找子节点
			setExpandByValues(item[props.childrenText], values)
		}
	}
}

function setPropertyByValue(value: string, setValue: boolean, prop: string) {
	findNodeByValue(store.value, value, prop, setValue)
}

function findNodeByValue(arr: Node[], value: string, prop: string, setValue) {
	for (const item of arr) {
		if (item[props.valueText] === value) {
			if (props.accordion && prop === 'isExpand') {
				arr.forEach(node => {
					node[prop] = false
				})
			}
			item[prop] = setValue
		}
		if (item[props.childrenText] && item[props.childrenText].length > 0) {
			// 递归查找子节点
			findNodeByValue(item[props.childrenText], value, prop, setValue)
		}
	}
}

function setCheckByParentAndChild(arr, value, setValue) {
	arr.forEach(item => {
		if (item[props.valueText] === value) {
			if (item[props.childrenText] && item[props.childrenText].length) {
				setChildCheck(item[props.childrenText], setValue)
			}
		} else {
			if (item[props.childrenText] && item[props.childrenText].length > 0) {
				setCheckByParentAndChild(item[props.childrenText], value, setValue)
			}
		}
	})
}

function setChildCheck(arr, setValue) {
	arr.forEach(item => {
		item.checked = setValue
		if (item[props.childrenText] && item[props.childrenText].length) {
			setChildCheck(item[props.childrenText], setValue)
		}
	})
}

function setParentCheck(arr) {
	arr.forEach(item => {
		if (item[props.childrenText] && item[props.childrenText].length) {
			let tem = item[props.childrenText].filter(k => !k.checked)
			if (tem.length) {
				item.checked = false
			} else {
				item.checked = true
			}
			setParentCheck(item[props.childrenText])
		}
	})
}

function getCheckValues(arr, newTem = []) {
	let tem = newTem
	arr.forEach(item => {
		if (item.checked) {
			tem.push(item[props.valueText])
		}
		if (item[props.childrenText] && item[props.childrenText].length) {
			getCheckValues(item[props.childrenText], tem)
		}
	})
	return tem
}

function getCurrentNode(arr, newTem = []) {
	let tem = newTem
	arr.forEach(item => {
		if (item.isSelect) {
			tem.push(item[props.valueText])
		} else {
			if (item[props.childrenText] && item[props.childrenText].length) {
				getCurrentNode(item[props.childrenText], tem)
			}
		}
	})
	return tem
}

function setCurrentNode(arr, value) {
	arr.forEach(item => {
		if (item[props.valueText] === value) {
			item.isSelect = true
		} else {
			item.isSelect = false
			if (item[props.childrenText] && item[props.childrenText].length) {
				setCurrentNode(item[props.childrenText], value)
			}
		}
	})
}

function setExpandByKeys(arr, values) {
	arr.forEach(item => {
		if (values.includes(item[props.valueText])) {
			item.isExpand = true
		} else {
			item.isExpand = false
		}
		if (item[props.childrenText] && item[props.childrenText].length) {
			setExpandByKeys(item[props.childrenText], values)
		}
	})
}

function getParentByValue(arr, value) {
	let parentValues = []
	function find(node, curParent) {
		if (node[props.valueText] === value) {
			parentValues.push(...curParent)
			return true
		} else {
			if (node[props.childrenText] && node[props.childrenText].length) {
				let newParent = [...curParent, node]
				node[props.childrenText].forEach(item => {
					if (find(item, newParent)) {
						return true
					}
				})
			}
		}
	}
	for (const root of arr) {
		if (find(root, [])) {
			break // 找到后终止遍历
		}
	}
	return parentValues
}

function setChildren(node: Node, children: Node[], needReset = false) {
	function find(arr) {
		arr.forEach(item => {
			if (item[props.valueText] === node[props.valueText]) {
				item[props.childrenText] = formatChildren(children, node.level)
				item.isExpand = true
				item.isLeaf = !(children && children.length)
				item.isNeedLoad = needReset
			} else {
				if (item[props.childrenText] && item[props.childrenText].length) {
					find(item[props.childrenText])
				}
			}
		})
	}
	find(store.value)
}

function formatChildren(children: Node[], level) {
	return children.map(item => {
		return {
			isLeaf: false,
			isExpand: false,
			checked: false,
			isSelect: false,
			...item,
			loading: false,
			isNeedLoad: item.isLeaf !== undefined ? !item.isLeaf : true,
			level: level + 1,
			[props.childrenText]: []
		}
	})
}

const filterStore = computed(() => {
	if (!filterText.value) {
		return store.value
	}
	return filterNodes(store.value, filterText.value)
})

function filterNodes(nodes: Node[], keyword: string): Node[] {
	return nodes.reduce<Node[]>((result, node) => {
		// 检查当前节点是否匹配
		const isMatch = props.filterNodeMethod ? props.filterNodeMethod(keyword, node) : node[props.labelText].includes(keyword)

		// 如果当前节点匹配
		if (isMatch) {
			result.push(node)
			return result
		}

		// 如果当前节点不匹配，检查子节点
		if (node[props.childrenText] && node[props.childrenText].length) {
			// 递归过滤子节点
			const filteredChildren = filterNodes(node[props.childrenText], keyword)

			// 如果有匹配的子节点
			if (filteredChildren.length) {
				// 创建新节点对象，只包含匹配的子节点
				const newNode = { ...node, [props.childrenText]: filteredChildren }
				result.push(newNode)
			}
		}

		return result
	}, [])
}

const filterText = ref('')
function filter(val) {
	filterText.value = val
}

function itemClick(node: Node) {
	emit('item-click', node)
}

function change(value, item) {
	emit('change', value, item)
}

function setStopChange(val) {
	stopChange.value = val
}

export interface ProvideType {
	setPropertyByValue: (value: string, setValue: boolean, prop: string) => void
	itemClick: (node: any) => void
	setCheckByParentAndChild: (arr: Node[], value: string, setVale: boolean) => void
	store: Ref<Node[]>
	change: (value, item: Node) => void
	setStopChange: (value: boolean) => void
	setChildren: (node: Node, children: Node[], needReset?: boolean) => void
	setParentCheck: (arr: Node[]) => void
	updateModelValue: () => void
}

provide<ProvideType>('RootTree', {
	setStopChange,
	setPropertyByValue,
	itemClick,
	setCheckByParentAndChild,
	store: store,
	change,
	setChildren,
	setParentCheck,
	updateModelValue
})

const emit = defineEmits(['item-click', 'change', 'update:modelValue'])
defineExpose({
	filter
})
</script>
